import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeftCircle,
  Calendar,
  CheckCircle2,
  Download,
  Edit,
  FileText,
  Layers,
  Upload,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAssignment, useLearnerAssignmentSubmission } from '@/hooks/useAssignments'
import { useCourse } from '@/hooks/useCourses'
import { useAuth } from '@/contexts/AuthContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { assignmentService } from '@/services/assignmentService'
import { paths } from '@/routes/path'
import BackButton from '@/components/ui/BackButton'

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  const decimals = value >= 10 || exponent === 0 ? 0 : 1
  return `${value.toFixed(decimals)} ${units[exponent]}`
}

export function AssignmentDetailsPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { addNotification } = useNotifications()

  const isInstructor = user?.role === 'admin' || user?.role === 'instructor'

  const {
    data: assignment,
    isLoading: assignmentLoading,
    error: assignmentError,
  } = useAssignment(assignmentId ?? '')

  const { data: mySubmission } = useLearnerAssignmentSubmission(assignmentId ?? '')
  const { data: course } = useCourse(assignment?.courseId)

  const [downloadInProgress, setDownloadInProgress] = useState<string | null>(null)

  const submissionStatus = useMemo(() => {
    if (!mySubmission) return null
    const label = mySubmission.status.charAt(0).toUpperCase() + mySubmission.status.slice(1)
    const submittedAt = mySubmission.submittedAt
      ? new Date(mySubmission.submittedAt).toLocaleString()
      : 'Not submitted'
    return { label, submittedAt }
  }, [mySubmission])

  const handleDownload = async (attachmentId: string, fileUrl: string, fileName: string) => {
    setDownloadInProgress(attachmentId)
    try {
      await assignmentService.downloadFile(fileUrl, fileName)
    } catch (error) {
      addNotification({
        title: 'Download failed',
        message: 'We could not download this file. Please try again.',
        type: 'general',
        priority: 'high',
        isRead: false,
        isArchived: false,
      })
    } finally {
      setDownloadInProgress(null)
    }
  }

  if (assignmentLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (assignmentError || !assignment) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md border-red-100 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              Assignment unavailable
            </CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not retrieve this assignment. It may have been unpublished or deleted.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const dueDate = new Date(assignment.dueDate)
  const isOverdue = dueDate < new Date()
  const courseTitle = course?.title ?? assignment.courseId

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <BackButton onClick={() => navigate(paths.app.assignments.root.absolute)}/>
            <div className="flex items-center gap-3 text-sm font-medium text-red-600">
              <Layers className="h-4 w-4" />
              {courseTitle}
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900">{assignment.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Due {dueDate.toLocaleString()}
            </span>
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {assignment.maxPoints} points
            </span>
            <Badge
              className={`${isOverdue && assignment.derivedStatus === 'open' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}
            >
              {assignment.derivedStatus === 'open' && isOverdue ? 'Overdue' : assignment.derivedStatus}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {
            isInstructor &&
            <Button
              className="w-full sm:w-auto"
              onClick={() => navigate(paths.app.assignments.submissions.list.build(assignment.id))}
            >
              View submissions
            </Button>
          }
          {isInstructor ? (
            <Button
              variant="outline"
              onClick={() => navigate(paths.app.assignments.edit.build(assignment.id))}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit assignment
            </Button>
          ) : (
            <Button
              onClick={() => navigate(paths.app.assignments.submit.build(assignment.id))}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Submit assignment
            </Button>
          )}
        </div>
      </header>

      {!isInstructor && submissionStatus && (
        <Card className="border-green-100 bg-green-50">
          <CardContent className="flex items-center justify-between py-4 text-sm text-green-700">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              <span>{submissionStatus.label}</span>
            </div>
            <span>Last update: {submissionStatus.submittedAt}</span>
          </CardContent>
        </Card>
      )}

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Assignment overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-sm text-gray-700">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Description</h2>
              <p className="mt-2 whitespace-pre-line">{assignment.description || 'No description supplied.'}</p>
            </div>
            {assignment.instructions && (
              <div>
                <h2 className="text-base font-semibold text-gray-900">Instructions</h2>
                <p className="mt-2 whitespace-pre-line">{assignment.instructions}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Quick facts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-gray-700">
            <div>
              <p className="font-medium text-gray-900">Course</p>
              <p>{courseTitle}</p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Due date</p>
              <p className={isOverdue ? 'text-red-600 font-medium' : ''}>{dueDate.toLocaleString()}</p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Maximum points</p>
              <p>{assignment.maxPoints}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {assignment.settings?.allowRetries && (
                <Badge className="bg-blue-100 text-blue-700">Retries allowed</Badge>
              )}
              {assignment.settings?.timeSensitive && (
                <Badge className="bg-orange-100 text-orange-700">Time sensitive</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      {assignment.attachments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Reference materials</CardTitle>
            <CardDescription>Download instructions, templates, or supporting documents.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            {assignment.attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center justify-between rounded-md border border-gray-200 px-4 py-3 text-sm"
              >
                <div>
                  <p className="font-medium text-gray-900">{attachment.title ?? attachment.fileName ?? 'Attachment'}</p>
                  <p className="text-xs text-gray-500">
                    {attachment.mimeType} • {formatBytes(attachment.fileSize)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleDownload(
                      attachment.id,
                      attachment.url,
                      attachment.title ?? attachment.fileName ?? 'attachment'
                    )
                  }
                  disabled={downloadInProgress === attachment.id}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloadInProgress === attachment.id ? 'Downloading...' : 'Download'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default AssignmentDetailsPage
